import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const PrivacyPolicy = () => {
  return (
    <>
      <Header />
      <div className="w-full min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-6 sm:px-10 lg:px-20 py-8">
      <h1 className="text-3xl font-bold mb-6">PRIVACY POLICY</h1>
     
        <p className="mb-4">
          This Privacy Notice for{" "}
          <span className="font-semibold">OnlySale</span> ("we," "us," or
          "our"), describes how and why we might access, collect, store, use,
          and/or share ("process") your personal information when you use our
          services ("Services"), including when you:
        </p>
        <ul className="list-disc list-inside mb-4 space-y-2">
          <li>
            Visit our website at{" "}
            <a
              href="https://www.theonlysale.com/"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              https://www.theonlysale.com/
            </a>
            , or any website of ours that links to this Privacy Notice
          </li>
          <li>
            Download and use our mobile application (
            <span className="font-semibold">OnlySale</span>), or any other
            application of ours that links to this Privacy Notice
          </li>
          <li>
            Engage with us in other related ways, including any sales,
            marketing, or events
          </li>
        </ul>

        <p className="mb-4">
          Questions or concerns? Reading this Privacy Notice will help you
          understand your privacy rights and choices. We are responsible for
          making decisions about how your personal information is processed. If
          you do not agree with our policies and practices, please do not use
          our Services. If you still have any questions or concerns, please
          contact us at{" "}
          <a
            href="mailto:oonlysale@gmail.com"
            className="text-blue-500 hover:underline"
          >
            oonlysale@gmail.com
          </a>
          .
        </p>
        <h2 className="text-2xl font-bold mt-8 mb-4">SUMMARY OF KEY POINTS</h2>
        <p className="mb-4">
          This summary provides key points from our Privacy Notice, but you can
          find out more details about any of these topics by clicking the link
          following each key point or by using our table of contents below to
          find the section you are looking for.
        </p>
        <h3 className="text-xl font-bold mt-6 mb-2">
          What personal information do we process?
        </h3>
        <p className="mb-4">
          When you visit, use, or navigate our Services, we may process personal
          information depending on how you interact with us and the Services,
          the choices you make, and the products and features you use. Learn
          more about personal information you disclose to us.
        </p>
        <h3 className="text-xl font-bold mt-6 mb-2">
          Do we process any sensitive personal information?
        </h3>
        <p className="mb-4">
          Some of the information may be considered "special" or "sensitive" in
          certain jurisdictions, for example your racial or ethnic origins,
          sexual orientation, and religious beliefs. We do not process sensitive
          personal information.
        </p>
        <h3 className="text-xl font-bold mt-6 mb-2">
          Do we collect any information from third parties?
        </h3>
        <p className="mb-4">
          We do not collect any information from third parties.
        </p>
        <h3 className="text-xl font-bold mt-6 mb-2">
          How do we process your information?
        </h3>
        <p className="mb-4">
          We process your information to provide, improve, and administer our
          Services, communicate with you, for security and fraud prevention, and
          to comply with law. We may also process your information for other
          purposes with your consent. We process your information only when we
          have a valid legal reason to do so. Learn more about how we process
          your information.
        </p>
        <h3 className="text-xl font-bold mt-6 mb-2">
          In what situations and with which parties do we share personal
          information?
        </h3>
        <p className="mb-4">
          We may share information in specific situations and with specific
          third parties. Learn more about when and with whom we share your
          personal information.
        </p>
        <h3 className="text-xl font-bold mt-6 mb-2">
          How do we keep your information safe?
        </h3>
        <p className="mb-4">
          We have adequate organizational and technical processes and procedures
          in place to protect your personal information. However, no electronic
          transmission over the internet or information storage technology can
          be guaranteed to be 100% secure, so we cannot promise or guarantee
          that hackers, cybercriminals, or other unauthorized third parties will
          not be able to defeat our security and improperly collect, access,
          steal, or modify your information. Learn more about how we keep your
          information safe.
        </p>
        <h3 className="text-xl font-bold mt-6 mb-2">What are your rights?</h3>
        <p className="mb-4">
          Depending on where you are located geographically, the applicable
          privacy law may mean you have certain rights regarding your personal
          information. Learn more about your privacy rights.
        </p>
        <h3 className="text-xl font-bold mt-6 mb-2">
          How do you exercise your rights?
        </h3>
        <p className="mb-4">
          The easiest way to exercise your rights is by submitting a data
          subject access request, or by contacting us. We will consider and act
          upon any request in accordance with applicable data protection laws.
        </p>
        <p className="mb-4">
          Want to learn more about what we do with any information we collect?
          Review the Privacy Notice in full.
        </p>
        <h2 className="text-2xl font-bold mt-8 mb-4">TABLE OF CONTENTS</h2>
        <ol className="list-decimal list-inside mb-4">
          <li>WHAT INFORMATION DO WE COLLECT?</li>
          <li>HOW DO WE PROCESS YOUR INFORMATION?</li>
          <li>
            WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?
          </li>
          <li>WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
          <li>DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
          <li>HOW LONG DO WE KEEP YOUR INFORMATION?</li>
          <li>HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
          <li>DO WE COLLECT INFORMATION FROM MINORS?</li>
          <li>WHAT ARE YOUR PRIVACY RIGHTS?</li>
          <li>CONTROLS FOR DO-NOT-TRACK FEATURES</li>
          <li>DO WE MAKE UPDATES TO THIS NOTICE?</li>
          <li>HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
          <li>
            HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
          </li>
        </ol>
        <h2 className="text-2xl font-bold mt-8 mb-4">
          1. WHAT INFORMATION DO WE COLLECT?
        </h2>
        <h3 className="text-xl font-bold mt-6 mb-2">
          Personal information you disclose to us
        </h3>
        <p className="mb-4">
          <span className="font-semibold">In Short:</span> We collect personal
          information that you provide to us.
        </p>
        <p className="mb-4">
          We collect personal information that you voluntarily provide to us
          when you register on the Services, express an interest in obtaining
          information about us or our products and Services, when you
          participate in activities on the Services, or otherwise when you
          contact us.
        </p>
        <h4 className="text-lg font-bold mt-4 mb-2">
          Personal Information Provided by You
        </h4>
        <p className="mb-4">
          The personal information that we collect depends on the context of
          your interactions with us and the Services, the choices you make, and
          the products and features you use. The personal information we collect
          may include the following:
        </p>
        <ul className="list-disc list-inside mb-4">
          <li>Names</li>
          <li>Phone numbers</li>
          <li>Email addresses</li>
          <li>Usernames</li>
          <li>Passwords</li>
          <li>Billing addresses</li>
          <li>Contact or authentication data</li>
        </ul>
        <h4 className="text-lg font-bold mt-4 mb-2">Sensitive Information</h4>
        <p className="mb-4">We do not process sensitive information.</p>
        <h4 className="text-lg font-bold mt-4 mb-2">Payment Data</h4>
        <p className="mb-4">
          We may collect data necessary to process your payment if you choose to
          make purchases, such as your payment instrument number, and the
          security code associated with your payment instrument. All payment
          data is handled and stored by{" "}
          <a
            href="https://stripe.com/legal/data-privacy-framework"
            className="text-blue-500 hover:underline"
          >
            Stripe
          </a>
          . You may find their privacy notice link(s) here:{" "}
          <a
            href="https://stripe.com/legal/data-privacy-framework"
            className="text-blue-500 hover:underline"
          >
            https://stripe.com/legal/data-privacy-framework
          </a>
          .
        </p>
        <h4 className="text-lg font-bold mt-4 mb-2">Application Data</h4>
        <p className="mb-4">
          If you use our application(s), we also may collect the following
          information if you choose to provide us with access or permission:
        </p>
        <ul className="list-disc list-inside mb-4">
          <li>
            <span className="font-semibold">Push Notifications:</span> We may
            request to send you push notifications regarding your account or
            certain features of the application(s). If you wish to opt out from
            receiving these types of communications, you may turn them off in
            your device's settings.
          </li>
        </ul>
        <p className="mb-4">
          This information is primarily needed to maintain the security and
          operation of our application(s), for troubleshooting, and for our
          internal analytics and reporting purposes.
        </p>
        <p className="mb-4">
          All personal information that you provide to us must be true,
          complete, and accurate, and you must notify us of any changes to such
          personal information.
        </p>
        {/* Section 2: HOW DO WE PROCESS YOUR INFORMATION? */}
        <h2 className="text-2xl font-bold mt-8 mb-4">
          2. HOW DO WE PROCESS YOUR INFORMATION?
        </h2>
        <p className="mb-4">
          <span className="font-semibold">In Short:</span> We process your
          information to provide, improve, and administer our Services,
          communicate with you, for security and fraud prevention, and to comply
          with law. We may also process your information for other purposes with
          your consent.
        </p>
        <p className="mb-4">
          We process your personal information for a variety of reasons,
          depending on how you interact with our Services, including:
        </p>
        <ul className="list-disc list-inside mb-4">
          <li>
            To facilitate account creation and authentication and otherwise
            manage user accounts. We may process your information so you can
            create and log in to your account, as well as keep your account in
            working order.
          </li>
          <li>
            To deliver and facilitate delivery of services to the user. We may
            process your information to provide you with the requested service.
          </li>
          <li>
            To respond to user inquiries/offer support to users. We may process
            your information to respond to your inquiries and solve any
            potential issues you might have with the requested service.
          </li>
          <li>
            To send administrative information to you. We may process your
            information to send you details about our products and services,
            changes to our terms and policies, and other similar information.
          </li>
          <li>
            To fulfill and manage your orders. We may process your information
            to fulfill and manage your orders, payments, returns, and exchanges
            made through the Services.
          </li>
          <li>
            To send you marketing and promotional communications. We may process
            the personal information you send to us for our marketing purposes,
            if this is in accordance with your marketing preferences. You can
            opt out of our marketing emails at any time. For more information,
            see "WHAT ARE YOUR PRIVACY RIGHTS?" below.
          </li>
          <li>
            To protect our Services. We may process your information as part of
            our efforts to keep our Services safe and secure, including fraud
            monitoring and prevention.
          </li>
          <li>
            To identify usage trends. We may process information about how you
            use our Services to better understand how they are being used so we
            can improve them.
          </li>
          <li>
            To save or protect an individual's vital interest. We may process
            your information when necessary to save or protect an individual's
            vital interest, such as to prevent harm.
          </li>
        </ul>
        {/* Section 3: WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION? */}
        <h2 className="text-2xl font-bold mt-8 mb-4">
          3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?
        </h2>
        <p className="mb-4">
          <span className="font-semibold">In Short:</span> We only process your
          personal information when we believe it is necessary and we have a
          valid legal reason (i.e., legal basis) to do so under applicable law,
          like with your consent, to comply with laws, to provide you with
          services to enter into or fulfill our contractual obligations, to
          protect your rights, or to fulfill our legitimate business interests.
        </p>
        <p className="mb-4">
          The General Data Protection Regulation (GDPR) and UK GDPR require us
          to explain the valid legal bases we rely on in order to process your
          personal information. As such, we may rely on the following legal
          bases to process your personal information:
        </p>
        <ul className="list-disc list-inside mb-4">
          <li>
            <span className="font-semibold">Consent:</span> We may process your
            information if you have given us permission (i.e., consent) to use
            your personal information for a specific purpose. You can withdraw
            your consent at any time. Learn more about withdrawing your consent.
          </li>
          <li>
            <span className="font-semibold">Performance of a Contract:</span> We
            may process your personal information when we believe it is
            necessary to fulfill our contractual obligations to you, including
            providing our Services or at your request prior to entering into a
            contract with you.
          </li>
          <li>
            <span className="font-semibold">Legitimate Interests:</span> We may
            process your information when we believe it is reasonably necessary
            to achieve our legitimate business interests and those interests do
            not outweigh your interests and fundamental rights and freedoms. For
            example, we may process your personal information for some of the
            purposes described in order to:
            <ul className="list-disc list-inside ml-6 mt-2">
              <li>
                Send users information about special offers and discounts on our
                products and services.
              </li>
              <li>
                Analyze how our Services are used so we can improve them to
                engage and retain users.
              </li>
              <li>Diagnose problems and/or prevent fraudulent activities.</li>
            </ul>
          </li>
          <li>
            <span className="font-semibold">Legal Obligations:</span> We may
            process your information where we believe it is necessary for
            compliance with our legal obligations, such as to cooperate with a
            law enforcement body or regulatory agency, exercise or defend our
            legal rights, or disclose your information as evidence in litigation
            in which we are involved.
          </li>
          <li>
            <span className="font-semibold">Vital Interests:</span> We may
            process your information where we believe it is necessary to protect
            your vital interests or the vital interests of a third party, such
            as situations involving potential threats to the safety of any
            person.
          </li>
        </ul>
        {/* Section 4: WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION? */}
        <h2 className="text-2xl font-bold mt-8 mb-4">
          4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
        </h2>
        <p className="mb-4">
          <span className="font-semibold">In Short:</span> We may share
          information in specific situations described in this section and/or
          with the following third parties.
        </p>
        <p className="mb-4">
          We may need to share your personal information in the following
          situations:
        </p>
        <ul className="list-disc list-inside mb-4">
          <li>
            <span className="font-semibold">Business Transfers:</span> We may
            share or transfer your information in connection with, or during
            negotiations of, any merger, sale of company assets, financing, or
            acquisition of all or a portion of our business to another company.
          </li>
        </ul>

        {/* Section 5: DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES? */}
        <h2 className="text-2xl font-bold mt-8 mb-4">
          5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
        </h2>
        <p className="mb-4">
          <span className="font-semibold">In Short:</span> We may use cookies
          and other tracking technologies to collect and store your information.
        </p>
        <p className="mb-4">
          We may use cookies and similar tracking technologies (like web beacons
          and pixels) to gather information when you interact with our Services.
          Some online tracking technologies help us maintain the security of our
          Services and your account, prevent crashes, fix bugs, save your
          preferences, and assist with basic site functions.
        </p>
        <p className="mb-4">
          We also permit third parties and service providers to use online
          tracking technologies on our Services for analytics and advertising,
          including to help manage and display advertisements, to tailor
          advertisements to your interests, or to send abandoned shopping cart
          reminders (depending on your communication preferences). The third
          parties and service providers use their technology to provide
          advertising about products and services tailored to your interests
          which may appear either on our Services or on other websites. Specific
          information about how we use such technologies and how you can refuse
          certain cookies is set out in our Cookie Notice.
        </p>

        {/* Section 6: HOW LONG DO WE KEEP YOUR INFORMATION? */}
        <h2 className="text-2xl font-bold mt-8 mb-4">
          6. HOW LONG DO WE KEEP YOUR INFORMATION?
        </h2>
        <p className="mb-4">
          <span className="font-semibold">In Short:</span> We keep your
          information for as long as necessary to fulfill the purposes outlined
          in this Privacy Notice unless otherwise required by law.
        </p>
        <p className="mb-4">
          We will only keep your personal information for as long as it is
          necessary for the purposes set out in this Privacy Notice, unless a
          longer retention period is required or permitted by law (such as tax,
          accounting, or other legal requirements). No purpose in this notice
          will require us keeping your personal information for longer than
          three (3) months past the start of the idle period of the user's
          account.
        </p>
        <p className="mb-4">
          When we have no ongoing legitimate business need to process your
          personal information, we will either delete or anonymize such
          information, or, if this is not possible (for example, because your
          personal information has been stored in backup archives), then we will
          securely store your personal information and isolate it from any
          further processing until deletion is possible.
        </p>

        {/* Section 7: HOW DO WE KEEP YOUR INFORMATION SAFE? */}
        <h2 className="text-2xl font-bold mt-8 mb-4">
          7. HOW DO WE KEEP YOUR INFORMATION SAFE?
        </h2>
        <p className="mb-4">
          <span className="font-semibold">In Short:</span> We aim to protect
          your personal information through a system of organizational and
          technical security measures.
        </p>
        <p className="mb-4">
          We have implemented appropriate and reasonable technical and
          organizational security measures designed to protect the security of
          any personal information we process. However, despite our safeguards
          and efforts to secure your information, no electronic transmission
          over the Internet or information storage technology can be guaranteed
          to be 100% secure, so we cannot promise or guarantee that hackers,
          cybercriminals, or other unauthorized third parties will not be able
          to defeat our security and improperly collect, access, steal, or
          modify your information. Although we will do our best to protect your
          personal information, transmission of personal information to and from
          our Services is at your own risk. You should only access the Services
          within a secure environment.
        </p>

        {/* Section 8: DO WE COLLECT INFORMATION FROM MINORS? */}
        <h2 className="text-2xl font-bold mt-8 mb-4">
          8. DO WE COLLECT INFORMATION FROM MINORS?
        </h2>
        <p className="mb-4">
          <span className="font-semibold">In Short:</span> We do not knowingly
          collect data from or market to children under 18 years of age.
        </p>
        <p className="mb-4">
          We do not knowingly collect, solicit data from, or market to children
          under 18 years of age, nor do we knowingly sell such personal
          information. By using the Services, you represent that you are at
          least 18 or that you are the parent or guardian of such a minor and
          consent to such minor dependent's use of the Services. If we learn
          that personal information from users less than 18 years of age has
          been collected, we will deactivate the account and take reasonable
          measures to promptly delete such data from our records. If you become
          aware of any data we may have collected from children under age 18,
          please contact us at conlysale@gmail.com.
        </p>

        {/* Section 9: WHAT ARE YOUR PRIVACY RIGHTS? */}
        <h2 className="text-2xl font-bold mt-8 mb-4">
          9. WHAT ARE YOUR PRIVACY RIGHTS?
        </h2>
        <p className="mb-4">
          <span className="font-semibold">In Short:</span> In some regions, you
          have rights that allow you greater access to and control over your
          personal information.
        </p>
        <p className="mb-4">
          In some regions, you have certain rights under applicable data
          protection laws. These may include the right (1) to request access and
          obtain a copy of your personal information, (2) to request
          rectification or erasure, (3) to restrict the processing of your
          personal information, (4) to data portability, and (5) not to be
          subject to automated decision-making. You can make such a request by
          contacting us using the contact details provided in the "HOW CAN YOU
          CONTACT US ABOUT THIS NOTICE?" section below.
        </p>

        {/* Section 10: CONTROLS FOR DO-NOT-TRACK FEATURES */}
        <h2 className="text-2xl font-bold mt-8 mb-4">
          10. CONTROLS FOR DO-NOT-TRACK FEATURES
        </h2>
        <p className="mb-4">
          <span className="font-semibold">In Short:</span> Most web browsers and
          some mobile operating systems and mobile applications include a
          Do-Not-Track ("DNT") feature or setting you can activate to signal
          your privacy preference not to have data about your online browsing
          activities monitored and collected.
        </p>
        <p className="mb-4">
          At this stage, no uniform technology standard for recognizing and
          implementing DNT signals has been finalized. As such, we do not
          currently respond to DNT browser signals or any other mechanism that
          automatically communicates your choice not to be tracked online. If a
          standard for online tracking is adopted that we must follow in the
          future, we will inform you in a revised version of this Privacy
          Notice.
        </p>

        {/* Section 11: DO WE MAKE UPDATES TO THIS NOTICE? */}
        <h2 className="text-2xl font-bold mt-8 mb-4">
          11. DO WE MAKE UPDATES TO THIS NOTICE?
        </h2>
        <p className="mb-4">
          <span className="font-semibold">In Short:</span> Yes, we will update
          this notice as necessary to stay compliant with relevant laws.
        </p>
        <p className="mb-4">
          We may update this Privacy Notice from time to time. The updated
          version will be indicated by an updated "Revised" date at the top of
          this Privacy Notice. If we make material changes to this Privacy
          Notice, we may notify you either by prominently posting a notice of
          such changes or by directly sending you a notification. We encourage
          you to review this Privacy Notice frequently to stay informed about
          how we are protecting your information.
        </p>

        {/* Section 12: HOW CAN YOU CONTACT US ABOUT THIS NOTICE? */}
        <h2 className="text-2xl font-bold mt-8 mb-4">
          12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
        </h2>
        <p className="mb-4">
          If you have questions or comments about this notice, you may email us
          at{" "}
          <a
            href="mailto:oonlysale@gmail.com"
            className="text-blue-500 hover:underline"
          >
            oonlysale@gmail.com
          </a>{" "}
          or contact us by post at:
        </p>
        <p className="mb-4">
          OnlySale
          <br />
          Prishtina, Kosovo 10000
          <br />
          Kosovo
        </p>

        {/* Section 13: HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU? */}
        <h2 className="text-2xl font-bold mt-8 mb-4">
          13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
          YOU?
        </h2>
        <p className="mb-4">
          Based on the applicable laws of your country, you may have the right
          to request access to the personal information we collect from you,
          details about how we have processed it, correct inaccuracies, or
          delete your personal information. You may also have the right to
          withdraw your consent to our processing of your personal information.
          These rights may be limited in some circumstances by applicable law.
          To request to review, update, or delete your personal information,
          please fill out and submit a{" "}
          <a href="#" className="text-blue-500 hover:underline">
            data subject access request
          </a>
          .
        </p>
      </div>
      <Footer />
    </>
  );
};
export default PrivacyPolicy;
